import { api } from "@/client/api";
import { ClerkWrapper } from "@/client/components/clerk-wrapper";
import { Shell } from "@/client/components/shell";
import { DatadogLogs } from "@/client/datadog-logs";
import { PROJECT_CONFIG } from "@/config";
import { createTheme, Group, MantineProvider, Stack } from "@mantine/core";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

import { Notifications } from "@mantine/notifications";
import { IconCards } from "@tabler/icons-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import type { AppType } from "next/app";
import dynamic from "next/dynamic";
import Head from "next/head";

export const queryClient = new QueryClient();

const theme = createTheme({
  respectReducedMotion: true,
  components: {
    Group: Group.extend({
      defaultProps: {
        gap: "sm",
      },
    }),
    Stack: Stack.extend({
      defaultProps: {
        gap: "sm",
      },
    }),
  },
});

const MyApp: AppType = ({ Component, pageProps: { ...pageProperties } }) => {
  // https://stackoverflow.com/a/71869550/6402548
  const AnyComponent = Component as any;
  return (
    <MantineProvider theme={theme}>
      <ClerkWrapper>
        <Analytics />
        <DatadogLogs />
        <Notifications />
        <QueryClientProvider client={queryClient}>
          <Shell icon={<IconCards />}>
            <Head>
              <title>
                {`${PROJECT_CONFIG.name} - ${PROJECT_CONFIG.shortDescription}`}
              </title>
            </Head>
            <AnyComponent {...pageProperties} />
          </Shell>
        </QueryClientProvider>
      </ClerkWrapper>
    </MantineProvider>
  );
};

/**
 * SSR causes more issues (particularly difficult-to-debug hydration issues) than it's worth, considering my site's core value is after a dynamic data load and the normal `<head>` tags should be sufficient for SEO, especially considering a considerable portion of my traffic is via word of mouth.
 *
 * Implementation Ref: https://stackoverflow.com/questions/53139884/next-js-disable-server-side-rendering-on-some-pages
 */
export default dynamic(() => Promise.resolve(api.withTRPC(MyApp)), {
  ssr: false,
});
